import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { KepDataType, RiwayatKodeKepDataType } from './types'
import type { FilterKepValues, MutationKepValues } from './validation'

import { aset } from './aset'
import { kegiatanUsaha } from './kegiatan-usaha'
import { kemitraanUsaha } from './kemitraan-usaha'
import { pengurusKep } from './pengurus-kep'
import { sertifikasi } from './sertifikasi'

const KEP_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['kep', key] as const

const getAll = (
  params?: FilterKepValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<KepDataType>>> => ({
  queryKey: KEP_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetAllResponse<Array<KepDataType>>>(
        '/kelembagaan/kep',
        {
          params
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<KepDataType>> => ({
  queryKey: KEP_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<KepDataType>>(
        `/kelembagaan/kep/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KepDataType>,
  MutationKepValues
> => ({
  mutationFn: async (data: MutationKepValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<KepDataType>>(
        '/kelembagaan/kep',
        {
          ...data,
          ada_badan_hukum: data.ada_badan_hukum === 'y',
          id_jenis_kelembagaan: data.id_jenis_kelembagaan.value,
          id_desa: data.id_desa.value,
          latitude: String(data?.latitude ?? ''),
          longitude: String(data?.longitude ?? ''),
          no_sk_pembentukan: data.no_sk_pembentukan || undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KepDataType>,
  MutationKepValues
> => ({
  mutationFn: async (data: MutationKepValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<KepDataType>>(
        `/kelembagaan/kep/${data.id_kep}`,
        {
          ...data,
          ada_badan_hukum: data.ada_badan_hukum === 'y',
          id_jenis_kelembagaan: data.id_jenis_kelembagaan.value,
          id_desa: data.id_desa.value,
          latitude: String(data?.latitude ?? ''),
          longitude: String(data?.longitude ?? ''),
          no_sk_pembentukan: data.no_sk_pembentukan || undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KepDataType>,
  { id_kep: string }
> => ({
  mutationFn: async (data: { id_kep: string }) => {
    try {
      const response = await api.delete<ServiceGetByIdResponse<KepDataType>>(
        `/kelembagaan/kep/${data.id_kep}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params?: FilterKepValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/kep/export`, { params })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getRiwayatKodeKep = (
  params: { id_kep: string; page?: number; per_page?: number }
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<RiwayatKodeKepDataType>>
> => ({
  queryKey: KEP_QUERY_KEY({ ...params, type: 'riwayat-kode' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<RiwayatKodeKepDataType>>
      >(`/kelembagaan/kep/${params.id_kep}/code-logs`, {
        params: {
          page: params.page,
          per_page: params.per_page
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const kep = {
  queryKey: [KEP_QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData,
  exportData,
  kegiatanUsaha,
  kemitraanUsaha,
  sertifikasi,
  pengurusKep,
  aset,
  getRiwayatKodeKep
}
